import { _js } from '@ifixit/utils/localize';
import { createContext, useRef, useState } from 'react';
import type { ComponentType } from 'react';
import { trackAnalyticsCustomEvent } from '@ifixit/analytics';
import { requireAuth } from 'Shared/require-auth';
import LoadingIndicator from 'Shared/loading_indicator';
import ApiRequest from 'Shared/api_2_0';

/** Where on the guide page someone marked it done, so the Workbench prompt shows next to it. */
type CompletionPlace = 'header' | 'conclusion';

const noop = () => {};

const defaultContext = {
   isCompleted: false,
   isPending: false,
   completedFrom: null as CompletionPlace | null,
   complete: noop as (_guideId: number, _place: CompletionPlace, _onComplete?: () => void) => void,
   removeComplete: noop as (_guideId: number) => void,
   completedCount: 0,
};

const GuideCompletionContext = createContext(defaultContext);

interface CompletionRequest {
   /** Returns false when another completion request is still running. */
   start: () => boolean;
   onSuccess: () => void;
   onSettled: () => void;
}

function doCompletionGuide(
   guideId: number,
   wantsToComplete: boolean,
   { start, onSuccess, onSettled }: CompletionRequest
) {
   requireAuth({
      from: '',
      message: _js('Log in to record that you completed a repair.'),
      onAuthorize: LoadingIndicator.withEventHandler(
         () => {
            if (!start()) return null;
            const method = wantsToComplete ? 'PUT' : 'DELETE';
            const url = `guides/${guideId}/completed`;

            const request = new ApiRequest(url, { method: method });
            return request
               .send({ langid: App.langid })
               .then(() => {
                  trackAnalyticsCustomEvent({
                     eventCategory: 'Guide Author Reward',
                     eventAction: wantsToComplete
                        ? 'Guide Author Reward - Form "Give the author +30 points" - Submit'
                        : 'Guide Author Reward - Cancel points',
                  });
                  onSuccess();
               })
               .finally(onSettled);
         },
         {
            pendingMessage: wantsToComplete ? _js('Adding Completion') : _js('Removing Completion'),
            successMessage: wantsToComplete ? _js('Added Completion') : _js('Removed Completion'),
            failureMessage: _js('Failure'),
         }
      ),
   });
}

function withGuideCompletion<T>(Component: ComponentType<T>) {
   return function GuideCompletionProvider(
      props: { isCompleted?: boolean; completedCount: number } & T
   ) {
      const [isCompleted, setIsCompleted] = useState(
         props.isCompleted || defaultContext.isCompleted
      );

      const [completedCount, setCompletedCount] = useState(
         props.completedCount || defaultContext.completedCount
      );

      const [completedFrom, setCompletedFrom] = useState<CompletionPlace | null>(null);

      // A ref, not just state: two clicks in one tick both run before a re-render.
      const pendingRef = useRef(false);
      const [isPending, setIsPending] = useState(false);

      const send = (guideid: number, wantsToComplete: boolean, onSuccess: () => void) =>
         doCompletionGuide(guideid, wantsToComplete, {
            start: () => {
               if (pendingRef.current) return false;
               pendingRef.current = true;
               setIsPending(true);
               return true;
            },
            onSuccess: () => {
               setIsCompleted(wantsToComplete);
               setCompletedCount(count => count + (wantsToComplete ? 1 : -1));
               onSuccess();
            },
            onSettled: () => {
               pendingRef.current = false;
               setIsPending(false);
            },
         });

      const guideCompletionContext = {
         complete: (guideid: number, place: CompletionPlace, onComplete?: () => void) =>
            send(guideid, true, () => {
               setCompletedFrom(place);
               onComplete?.();
            }),
         removeComplete: (guideid: number) => send(guideid, false, () => setCompletedFrom(null)),
         isCompleted,
         isPending,
         completedFrom,
         completedCount,
      };

      return (
         <GuideCompletionContext.Provider value={guideCompletionContext}>
            <Component {...props} />
         </GuideCompletionContext.Provider>
      );
   };
}

export { GuideCompletionContext, withGuideCompletion };
